import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Paper,
  Container,
  Chip,
  Divider,
  Avatar,
  Badge,
  IconButton,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  LocalOffer as OfferIcon,
  Star as StarIcon,
  Favorite as FavoriteIcon,
  ShoppingCart as CartIcon,
  Timer as TimerIcon,
  EmojiFoodBeverage as FoodIcon
} from "@mui/icons-material";

const TiffinService = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const menuItems = [
    { 
      id: 1, 
      name: "Vegetable Curry", 
      price: "$5", 
      color: "#FF8C00",
      popular: true,
      veg: true,
      prepTime: "20 min"
    },
    { 
      id: 2, 
      name: "Paneer Masala", 
      price: "$6", 
      color: "#FF4500",
      popular: true,
      veg: true,
      prepTime: "25 min"
    },
    { 
      id: 3, 
      name: "Dal Tadka", 
      price: "$4", 
      color: "#32CD32",
      veg: true,
      prepTime: "15 min"
    },
    { 
      id: 4, 
      name: "Rice", 
      price: "$2", 
      color: "#FFD700",
      veg: true,
      prepTime: "10 min"
    },
    { 
      id: 5, 
      name: "Chapati", 
      price: "$1", 
      color: "#1E90FF",
      veg: true,
      prepTime: "5 min"
    },
    { 
      id: 6, 
      name: "Salad", 
      price: "$2", 
      color: "#FF69B4",
      veg: true,
      prepTime: "8 min"
    },
    { 
      id: 7, 
      name: "Paneer Tikka", 
      price: "$7", 
      color: "#8A2BE2",
      popular: true,
      veg: true,
      prepTime: "30 min"
    },
    { 
      id: 8, 
      name: "Biryani", 
      price: "$8", 
      color: "#FF1493",
      veg: false,
      prepTime: "40 min"
    },
  ];

  const offers = [
    {
      id: 1,
      title: "Special Offer!",
      description: "Buy 2 Get 1 Free",
      icon: <OfferIcon fontSize="large" />,
      color: "#4CAF50"
    },
    {
      id: 2,
      title: "Limited Time!",
      description: "20% Off on Biryani",
      icon: <TimerIcon fontSize="large" />,
      color: "#FF5722"
    },
    {
      id: 3,
      title: "New User",
      description: "15% Off First Order",
      icon: <StarIcon fontSize="large" />,
      color: "#2196F3"
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: 4,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        p: 3,
        borderRadius: 2,
        boxShadow: 1
      }}>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold', 
            color: theme.palette.primary.main,
            [theme.breakpoints.down('sm')]: {
              fontSize: '1rem'
            }
          }}
        >
          <FoodIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Tiffin Services
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Enjoy delicious home-cooked meals delivered to your doorstep
        </Typography>
      </Box>

      {/* Menu Grid */}
      <Grid container spacing={3} justifyContent="center">
        {menuItems.map((item) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: 6
                },
                position: 'relative',
                overflow: 'visible',
                borderTop: `4px solid ${item.color}`
              }}
            >
              {item.popular && (
                <Chip
                  label="Popular"
                  color="secondary"
                  size="small"
                  sx={{ 
                    position: 'absolute', 
                    top: -12, 
                    right: 10,
                    fontWeight: 'bold'
                  }}
                />
              )}
              
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                    {item.name}
                  </Typography>
                  <Chip 
                    label={item.veg ? "Veg" : "Non-Veg"} 
                    size="small" 
                    sx={{ 
                      backgroundColor: item.veg ? '#4CAF50' : '#F44336',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TimerIcon color="action" fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    {item.prepTime}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                    {item.price}
                  </Typography>
                  <Box>
                    <IconButton aria-label="add to favorites" size="small">
                      <FavoriteIcon fontSize="small" />
                    </IconButton>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<CartIcon />}
                      sx={{ ml: 1 }}
                    >
                      Order
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Offers Section */}
      <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
        <OfferIcon color="secondary" sx={{ mr: 1 }} />
        Special Offers
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {offers.map((offer) => (
          <Grid item xs={12} sm={6} md={4} key={offer.id}>
            <Paper
              sx={{
                p: 3,
                height: '100%',
                background: `linear-gradient(135deg, ${offer.color} 0%, ${theme.palette.getContrastText(offer.color) === '#fff' ? 
                  theme.palette.augmentColor({ color: { main: offer.color } }).dark : 
                  theme.palette.augmentColor({ color: { main: offer.color } }).light} 100%)`,
                color: theme.palette.getContrastText(offer.color),
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                minHeight: 150
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mb: 2 }}>
                {offer.icon}
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {offer.title}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {offer.description}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>


      <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
          <StarIcon color="primary" sx={{ mr: 1 }} />
          How to Order
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                1
              </Avatar>
              <Typography variant="body1">
                Browse our menu and select your favorite dishes
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                2
              </Avatar>
              <Typography variant="body1">
                Add items to cart and proceed to checkout
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                3
              </Avatar>
              <Typography variant="body1">
                Enjoy your meal delivered fresh to your doorstep
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Testimonials Section */}
      <Typography variant="h5" sx={{ mt: 4, mb: 3, fontWeight: 'bold' }}>
        What Our Customers Say
      </Typography>
      
      <Grid container spacing={3}>
        {[1, 2, 3].map((item) => (
          <Grid item xs={12} md={4} key={item}>
            <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar 
                  src={`https://i.pravatar.cc/150?img=${item + 10}`} 
                  sx={{ width: 56, height: 56, mr: 2 }} 
                />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Customer {item}
                  </Typography>
                  <Box sx={{ display: 'flex' }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIcon key={star} color={star <= 4 ? 'secondary' : 'disabled'} fontSize="small" />
                    ))}
                  </Box>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                "The food was delicious and arrived right on time. Highly recommend the Paneer Masala!"
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default TiffinService;